import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import AceEditor from 'react-ace';
import { MenuItem, Dialog, Tooltip, useTheme } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/mode-python';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/theme-twilight';
import 'ace-builds/src-noconflict/theme-solarized_dark';
import 'ace-builds/src-noconflict/theme-solarized_light';
import 'ace-builds/src-noconflict/ext-language_tools';
import {
  AceEditorContainer,
  EditorControlsContainer,
  EditorActionsContainer,
  CodeLanguageSelect,
  ThemeSelect,
  EditorActionButton,
  FullscreenDialogContent,
  EditorContainer,
} from '../CodeStyle';
import LanguageChangeDialog from './LanguageChangeDialog';
import { CODE_LANG, CODE_LANG_DEFAULT, aceNameForLang } from '../CodeUtil';

const DARK_DEFAULT_THEME = 'monokai';
const LIGHT_DEFAULT_THEME = 'github';
const DARK_THEMES = ['monokai', 'twilight', 'solarized_dark'];
const LIGHT_THEMES = ['github', 'solarized_light'];

const editorStyles = `
  .ace_editor, .ace_editor * {
    font-family: "Monaco", "Menlo", "Ubuntu Mono", "Droid Sans Mono", "Consolas", monospace !important;
    font-size: 16px !important;
    font-weight: 400 !important;
    letter-spacing: 0 !important;
  }
`;

const CodeEditor = ({ classes, onCodeChange, onLanguageChange, version, store }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const data = store ? store(state => state.data) : {};
  const safeData = data || {};
  const codeLang = safeData.language || CODE_LANG_DEFAULT;
  const code = safeData.code ?? '';
  const [aceLangStyle, setAceLangStyle] = useState(aceNameForLang(codeLang));
  const [willChangeLanguage, setWillChangeLanguage] = useState(false);
  const [changeLangValue, setChangeLangValue] = useState('');

  const [currentTheme, setCurrentTheme] = useState(() => {
    const savedTheme = localStorage.getItem('codeEditorTheme');
    if (savedTheme) return savedTheme;
    return isDark ? DARK_DEFAULT_THEME : LIGHT_DEFAULT_THEME;
  });
  const [isExpanded, setIsExpanded] = useState(false);

  // Auto-switch editor theme when app theme mode changes
  useEffect(() => {
    if (isDark && LIGHT_THEMES.includes(currentTheme)) {
      setCurrentTheme(DARK_DEFAULT_THEME);
      localStorage.setItem('codeEditorTheme', DARK_DEFAULT_THEME);
    } else if (!isDark && DARK_THEMES.includes(currentTheme)) {
      setCurrentTheme(LIGHT_DEFAULT_THEME);
      localStorage.setItem('codeEditorTheme', LIGHT_DEFAULT_THEME);
    }
  }, [isDark]);

  const handleLanguageChange = value => {
    setWillChangeLanguage(true);
    setChangeLangValue(value.target.value);
  };

  const confirmLanguageChange = () => {
    setWillChangeLanguage(false);
    const newValue = changeLangValue;
    let newStyle = aceLangStyle;

    newStyle = aceNameForLang(newValue);

    setAceLangStyle(newStyle);

    onLanguageChange(newValue);
  };

  const cancelLanguageChange = () => {
    setWillChangeLanguage(false);
  };

  const handleThemeChange = event => {
    const newTheme = event.target.value;
    localStorage.setItem('codeEditorTheme', newTheme);
    setCurrentTheme(newTheme);
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const editorComponent = (
    <AceEditor
      mode={aceLangStyle}
      wrapEnabled={true}
      theme={currentTheme}
      width="100%"
      height={isExpanded ? '80vh' : '100%'}
      onChange={value => {
        onCodeChange(value, version);
      }}
      showPrintMargin={false}
      showGutter={true}
      highlightActiveLine={true}
      value={code}
      fontSize={16}
      setOptions={{
        enableBasicAutocompletion: true,
        enableLiveAutocompletion: true,
        enableSnippets: false,
        showLineNumbers: true,
        tabSize: 2,
      }}
    />
  );

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = editorStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <Grid container rowSpacing={2} alignItems="center">
      <Grid item xs={12}>
        <div className={classes?.label ?? ''}>Code</div>
      </Grid>

      <Grid item xs={12}>
        <EditorControlsContainer>
          <CodeLanguageSelect
            name="codeLang"
            value={codeLang}
            defaultValue={CODE_LANG_DEFAULT}
            disableUnderline
            variant="standard"
            onChange={handleLanguageChange}
          >
            <MenuItem value={CODE_LANG.NODE.value}>{CODE_LANG.NODE.label}</MenuItem>
            <MenuItem value={CODE_LANG.PYTHON.value}>{CODE_LANG.PYTHON.label}</MenuItem>
          </CodeLanguageSelect>

          <EditorActionsContainer>
            <ThemeSelect
              value={currentTheme}
              onChange={handleThemeChange}
              disableUnderline
              variant="standard"
            >
              <MenuItem value="github">Light</MenuItem>
              <MenuItem value="monokai">Dark</MenuItem>
              <MenuItem value="twilight">Twilight</MenuItem>
              <MenuItem value="solarized_light">Solarized Light</MenuItem>
              <MenuItem value="solarized_dark">Solarized Dark</MenuItem>
            </ThemeSelect>

            <Tooltip title={isExpanded ? 'Exit Fullscreen' : 'Fullscreen'}>
              <EditorActionButton onClick={toggleExpand} size="small">
                {isExpanded ? (
                  <FullscreenExitIcon fontSize="small" />
                ) : (
                  <FullscreenIcon fontSize="small" />
                )}
              </EditorActionButton>
            </Tooltip>
          </EditorActionsContainer>
        </EditorControlsContainer>
      </Grid>

      <Grid item xs={12}>
        <AceEditorContainer id="aceditor">{!isExpanded && editorComponent}</AceEditorContainer>
      </Grid>

      <LanguageChangeDialog
        isOpen={willChangeLanguage}
        onCancel={cancelLanguageChange}
        onConfirm={confirmLanguageChange}
        loadMoreClass={classes?.loadMore ?? ''}
      />

      <Dialog
        open={isExpanded}
        onClose={toggleExpand}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          style: {
            boxShadow: 'none',
            border: '1px solid var(--color-divider)',
          },
        }}
      >
        <FullscreenDialogContent>
          <EditorControlsContainer>
            <CodeLanguageSelect
              name="codeLangDialog"
              value={codeLang}
              disableUnderline
              variant="standard"
              onChange={handleLanguageChange}
            >
              <MenuItem value={CODE_LANG.NODE.value}>{CODE_LANG.NODE.label}</MenuItem>
              <MenuItem value={CODE_LANG.PYTHON.value}>{CODE_LANG.PYTHON.label}</MenuItem>
            </CodeLanguageSelect>

            <EditorActionsContainer>
              <ThemeSelect
                value={currentTheme}
                onChange={handleThemeChange}
                disableUnderline
                variant="standard"
              >
                <MenuItem value="github">Light</MenuItem>
                <MenuItem value="monokai">Dark</MenuItem>
                <MenuItem value="twilight">Twilight</MenuItem>
                <MenuItem value="solarized_light">Solarized Light</MenuItem>
                <MenuItem value="solarized_dark">Solarized Dark</MenuItem>
              </ThemeSelect>

              <Tooltip title="Exit Fullscreen">
                <EditorActionButton onClick={toggleExpand}>
                  <FullscreenExitIcon fontSize="small" />
                </EditorActionButton>
              </Tooltip>
            </EditorActionsContainer>
          </EditorControlsContainer>

          <EditorContainer>{isExpanded && editorComponent}</EditorContainer>
        </FullscreenDialogContent>
      </Dialog>
    </Grid>
  );
};

export default CodeEditor;
